import { useState } from 'react';
import {
  ReleaseBody, SiteRelease, createSiteRelease, deleteSiteRelease, saveSiteRelease,
} from '../../../api/platform';
import { Modal, RowMenu, TableCard } from '../../../components/fs';
import { PHONE, useMediaQuery } from '../../../shared/media';

/**
 * Site content › Releases (the handoff's tab 4, R12's card unchanged): the
 * release history the community page lists, newest first, and the sign-in
 * page's "What's new". "+ New release" moved from the page head into this
 * card's head; a new release starts at the next version (the latest + 0.1)
 * and today. On a phone the card is a list of rows (ABC4-06), a branch
 * rendered only while PHONE matches.
 */

type Editor = { kind: 'release'; id: number | null } | { kind: 'release-delete'; id: number } | null;

const dateLabel = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const lines = (n: number) => `${n} ${n === 1 ? 'line' : 'lines'}`;
const cut = (s: string) => `${s.slice(0, 90)}${s.length > 90 ? '…' : ''}`;
/** A "+ New …" label whose accessible name is the words alone. */
const Plus = ({ children }: { children: string }) => <><span aria-hidden="true">+</span> {children}</>;

/** The version after the newest: "1.9" → "2.0"; nothing when it does not read as a number. */
export function nextVersion(releases: SiteRelease[]): string {
  const n = parseFloat(releases[0]?.version ?? '');
  return Number.isFinite(n) ? (n + 0.1).toFixed(1) : '';
}

export default function ReleasesTab({ releases, readOnly, write }: {
  releases: SiteRelease[] | null;
  readOnly: boolean;
  /** Run a write, reload, toast; resolves false (and keeps the dialog) when refused. */
  write: (fn: () => Promise<unknown>, done: string) => Promise<string | null>;
}) {
  const phone = useMediaQuery(PHONE);
  const [editor, setEditor] = useState<Editor>(null);
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [error, setError] = useState('');
  const edit = (next: Editor) => { setError(''); setEditor(next); };
  const run = async (fn: () => Promise<unknown>, done: string) => {
    const refused = await write(fn, done);
    if (refused) setError(refused); else edit(null);
  };

  const editing = editor?.kind === 'release' && editor.id !== null ? (releases || []).find((r) => r.id === editor.id) || null : null;
  const deleting = editor?.kind === 'release-delete' ? (releases || []).find((r) => r.id === editor.id) || null : null;

  const menu = (r: SiteRelease) => !readOnly && (
    <RowMenu open={openMenu === r.id} onToggle={(open) => setOpenMenu(open ? r.id : null)} label={`v${r.version} · ${r.title}`}>
      <button onClick={() => edit({ kind: 'release', id: r.id })}>Edit…</button>
      <div className="rule" />
      <button className="danger" onClick={() => edit({ kind: 'release-delete', id: r.id })}>Delete…</button>
    </RowMenu>
  );

  return (
    <>
      <TableCard
        className="sc-releases"
        title="Releases"
        // Offered once the list is in: a new release starts at the version after the newest.
        action={!readOnly && releases ? (
          <button type="button" className="fs-btn text" onClick={() => edit({ kind: 'release', id: null })}>
            <Plus>New release</Plus>
          </button>
        ) : undefined}
        footer={<span>Newest first on the public page. The latest release also shows as “What’s new” on the sign-in page.</span>}
      >
        {!releases ? <div className="st-card-body st-muted">Loading…</div>
          : releases.length === 0 ? <div className="st-card-body st-muted st-empty">No releases yet.</div>
            : phone ? (
              <div className="sc-rows">
                {releases.map((r) => (
                  <div className="sc-row" key={r.id}>
                    <div className="main">
                      <div className="title"><span className="fs-pill info">v{r.version}</span> <span>{r.title}</span></div>
                      <div className="meta">{dateLabel(r.released_on)} · {lines(r.highlights.length)}</div>
                      {r.summary && <div className="sub">{cut(r.summary)}</div>}
                    </div>
                    {menu(r)}
                  </div>
                ))}
              </div>
            ) : (
              <table className="fs-table">
                <thead>
                  <tr>
                    <th className="sc-col-version">Version</th><th>Title</th><th className="sc-col-date">Date</th>
                    <th className="sc-col-lines">Highlights</th>
                    <th className="sc-col-menu fs-row-actions" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {releases.map((r) => (
                    <tr key={r.id}>
                      <td><span className="fs-pill info">v{r.version}</span></td>
                      <td>
                        <div className="sc-title">{r.title}</div>
                        {r.summary && <div className="fs-cell-sub faint">{cut(r.summary)}</div>}
                      </td>
                      <td className="sc-date">{dateLabel(r.released_on)}</td>
                      <td>{lines(r.highlights.length)}</td>
                      <td className="fs-row-actions">{menu(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
      </TableCard>

      {editor?.kind === 'release' && (
        <ReleaseModal
          release={editing}
          releases={releases || []}
          error={error}
          onClose={() => edit(null)}
          onSave={(body) => run(() => (editing ? saveSiteRelease(editing.id, body) : createSiteRelease(body)),
            'Saved — live on the public site now.')}
        />
      )}
      {deleting && (
        <Modal
          narrow
          title={`Delete v${deleting.version}?`}
          onClose={() => edit(null)}
          footer={(
            <>
              <button className="fs-btn danger solid" onClick={() => run(() => deleteSiteRelease(deleting.id), 'Release deleted.')}>
                Delete release
              </button>
              <button className="fs-btn neutral" onClick={() => edit(null)}>Cancel</button>
            </>
          )}
        >
          {error && <div className="ad-err">{error}</div>}
          <p className="sc-confirm">
            “{deleting.title}” disappears from the public release history immediately.
            There is no undo — re-adding it means retyping it.
          </p>
        </Modal>
      )}
    </>
  );
}

function ReleaseModal({ release, releases, error, onClose, onSave }: {
  release: SiteRelease | null;
  releases: SiteRelease[];
  /** the server's refusal of the last save */
  error: string;
  onClose: () => void;
  onSave: (body: ReleaseBody) => void;
}) {
  const [version, setVersion] = useState(release?.version ?? nextVersion(releases));
  const [title, setTitle] = useState(release?.title || '');
  const [releasedOn, setReleasedOn] = useState(release?.released_on || new Date().toISOString().slice(0, 10));
  const [summary, setSummary] = useState(release?.summary || '');
  const [highlights, setHighlights] = useState((release?.highlights || []).join('\n'));

  const body = (): ReleaseBody => ({
    version: version.trim(),
    title: title.trim(),
    released_on: releasedOn,
    summary: summary.trim(),
    highlights: highlights.split('\n').map((h) => h.trim()).filter(Boolean),
  });
  // Said before the server has to: the version is the release's name.
  const dup = !!version.trim() && releases.some((r) => r.version === version.trim() && r.id !== release?.id);
  const valid = version.trim() && title.trim().length >= 2 && releasedOn && !dup;

  return (
    <Modal
      title={release ? `Edit v${release.version}` : 'New release'}
      onClose={onClose}
      footer={(
        <>
          <span className="sc-foot-hint">{dup ? `v${version.trim()} already exists.` : 'Live on the public release history as soon as you save.'}</span>
          <button className="fs-btn primary" disabled={!valid} onClick={() => onSave(body())}>
            {release ? 'Save release' : 'Publish release'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="sc-form">
        {error && <div className="ad-err">{error}</div>}
        <div className="sc-release-grid">
          <div className="fs-field">
            <label htmlFor="sc-rel-version">Version</label>
            <input id="sc-rel-version" type="text" value={version} placeholder="1.7" onChange={(e) => setVersion(e.target.value)} autoFocus={!release} />
          </div>
          <div className="fs-field">
            <label htmlFor="sc-rel-title">Title</label>
            <input id="sc-rel-title" type="text" value={title} placeholder="What this release is about" onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="fs-field">
            <label htmlFor="sc-rel-date">Released</label>
            <input id="sc-rel-date" type="date" value={releasedOn} onChange={(e) => setReleasedOn(e.target.value)} />
          </div>
        </div>
        <div className="fs-field">
          <label htmlFor="sc-rel-summary">Summary</label>
          <textarea id="sc-rel-summary" rows={2} value={summary} onChange={(e) => setSummary(e.target.value)}
            placeholder="One or two sentences under the heading. Optional." />
        </div>
        <div className="fs-field">
          <label htmlFor="sc-rel-highlights">Highlights — one per line</label>
          <textarea id="sc-rel-highlights" rows={6} value={highlights} onChange={(e) => setHighlights(e.target.value)}
            placeholder={'New field types: email, phone, number\nPer-field validation with clear inline errors'} />
          <span className="hint">Each line becomes one bullet on the public page.</span>
        </div>
      </div>
    </Modal>
  );
}
